import type { Role } from '@magic/schemas/messages';
import remarkGfm from 'remark-gfm';
import styled, { css } from 'styled-components';

import * as components from './components';
import { Loader } from './loader';
import { MemoizedReactMarkdown } from './markdown';
import { formatTimestamp } from './timestamp';

interface MessageProps {
  children: string;
  role: Role;
  timestamp?: Date;
}

const MessageContent = styled.div<{ role?: Role }>`
  border-radius: var(--border-radius-rounded);
  padding: var(--size-spacing-04);
  background-color: var(--neutral-surface-low-emphasis);
  overflow-wrap: break-word;
  word-break: break-word;
  color: inherit;
  width: auto;
  max-width: 90%;

  ${({ role }) =>
    role === 'user'
      ? css`
          color: var(--neutral-text-inverse);
          background-color: var(--neutral-surface-inverse);
          align-self: end;
          align-items: end;
        `
      : css`
          align-self: start;
          align-items: start;
        `}
`;

const MessageContainer = styled.div<{ role?: Role }>`
  display: flex;
  flex-direction: column;
  gap: var(--size-spacing-02);

  ${({ role }) =>
    role === 'user'
      ? css`
          align-self: end;
          align-items: end;
        `
      : css`
          align-self: start;
          align-items: start;
        `}
`;

export const Message = ({ children, role, timestamp }: MessageProps) => {
  return (
    <MessageContainer role={role}>
      <MessageContent
        role={role}
        className={role === 'user' ? 'prose prose-invert' : 'prose'}
        title={formatTimestamp(timestamp)}
      >
        <MemoizedReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={components}
        >
          {children}
        </MemoizedReactMarkdown>
      </MessageContent>
    </MessageContainer>
  );
};

export const Typing = () => (
  <MessageContainer>
    <MessageContent>
      <Loader />
    </MessageContent>
  </MessageContainer>
);
